// Packages
import {
  AttachInternals,
  Component,
  Element,
  Event as StencilEvent,
  EventEmitter,
  h,
  Host,
  Prop,
  State,
  Watch,
} from "@stencil/core";

// Types
import {
  TThemeColors,
  IRangeValue,
  IRangeChangeEventDetail,
  IRangeKnobMoveEventDetail,
  TRangeKnob,
} from "src/interface";

// Utils
import { buildColorClasses } from "@utils/theme";
import { debounce, DebouncedFunction } from "@utils/utils";

/**
 * `fx-range` is a headless, accessible slider component supporting single and
 * dual knobs, snapping, ticks, and a value pin. It ships no internal styles;
 * every styleable surface is exposed via `part` attributes and its state
 * through `data-*` and ARIA attributes so themes can customize it freely.
 *
 * @part base - The container that wraps the track, knobs, and ticks.
 * @part track - The full-width rail the knob(s) travel along.
 * @part active-track - The filled portion of the track between/under the knob(s).
 * @part ticks - The container for tick marks (only when `snaps` and `ticks`).
 * @part tick - A single tick mark.
 * @part tick-active - A tick mark at or within the current value range.
 * @part knob - A draggable knob. Rendered once (single) or twice (dual).
 * @part knob-lower - The lower knob (dual) or the single knob.
 * @part knob-upper - The upper knob (dual only).
 * @part pin - The value bubble shown above a knob when `pin` is enabled.
 */
// `styleUrl` is intentionally omitted (no internal styles per AGENTS.md).
@Component({
  tag: "fx-range",
  shadow: true,
  formAssociated: true,
})
export class FxRange {
  // --------------------------------------------
  // Element / internal references
  // --------------------------------------------

  @Element() el!: HTMLElement;

  @AttachInternals() internals!: ElementInternals;

  private trackRef?: HTMLElement;

  private knobRefs: Partial<Record<TRangeKnob, HTMLElement>> = {};

  private activeKnob: TRangeKnob | null = null;

  private hasFocus: boolean = false;

  // Cached during a pointer drag so we avoid a layout read on every move.
  private dragRect: DOMRect | null = null;

  // The element that captured the active pointer, plus its pointer id.
  private capturedEl: HTMLElement | null = null;

  private capturedPointerId: number | null = null;

  // Guards the `value` watcher from re-running when we mutate it internally.
  private isInternalValueUpdate: boolean = false;

  // Whether a debounced `fxChange` is scheduled but not yet emitted.
  private hasPendingChange: boolean = false;

  private emitDebouncedChange: DebouncedFunction<
    (detail: IRangeChangeEventDetail) => void
  > = Object.assign(() => undefined, {
    cancel: () => undefined,
    flush: () => undefined,
  });

  // --------------------------------------------
  // State
  // --------------------------------------------

  /**
   * The value of the lower knob (single knob uses this value as well).
   */
  @State()
  private valueLower: number = 0;

  /**
   * The value of the upper knob, used only when `dualKnobs` is `true`.
   */
  @State()
  private valueUpper: number = 100;

  /**
   * Whether a knob is currently being dragged or moved via the keyboard.
   */
  @State()
  private isActive: boolean = false;

  // --------------------------------------------
  // Props
  // --------------------------------------------

  /**
   * @prop - If `true`, the range is disabled and cannot be interacted with.
   * Reflected via `data-disabled` and `aria-disabled`.
   */
  @Prop()
  disabled: boolean = false;

  /**
   * @prop - The current value. A `number` for a single knob or an object
   * `{ lower, upper }` for dual knobs. `mutable` so the component can update it.
   */
  @Prop({ mutable: true })
  value?: number | IRangeValue;

  /**
   * @prop - The color theme. Applies `fx-color-{color}` and `fx-color-theme`
   * classes on the host and reflects to `data-color`.
   */
  @Prop()
  color?: TThemeColors | string;

  /**
   * @prop - Number of milliseconds to wait before emitting the `fxChange`
   * event. Uses the shared `debounce` utility.
   */
  @Prop()
  debounce: number = 0;

  /**
   * @prop - If `true`, the range exposes two knobs (a lower and an upper).
   * Reflected via `data-dual-knobs`.
   */
  @Prop()
  dualKnobs: boolean = false;

  /**
   * @prop - The maximum value.
   */
  @Prop()
  max: number = 100;

  /**
   * @prop - The minimum value.
   */
  @Prop()
  min: number = 0;

  /**
   * @prop - The name of the control, used when submitting a form.
   */
  @Prop()
  name?: string;

  /**
   * @prop - If `true`, a value pin is shown above the active knob.
   */
  @Prop()
  pin: boolean = false;

  /**
   * @prop - A function that formats the value displayed in the pin.
   * Defaults to `Math.round`.
   */
  @Prop()
  pinFormatter: (value: number) => string | number = (value: number) =>
    Math.round(value);

  /**
   * @prop - If `true`, values are quantized to the nearest `step` on the grid.
   * If `false`, the knob moves freely and is only clamped to `min`/`max`
   * (keyboard interaction still moves by `step`).
   */
  @Prop()
  snaps: boolean = false;

  /**
   * @prop - The step increment used for keyboard interaction and snapping.
   */
  @Prop()
  step: number = 1;

  /**
   * @prop - If `true`, tick marks are rendered. Only applies when `snaps` is
   * also `true`.
   */
  @Prop()
  ticks: boolean = false;

  // --------------------------------------------
  // Events
  // --------------------------------------------

  /**
   * @event - Emitted with the current value when it changes. Debounced by the
   * `debounce` prop.
   */
  @StencilEvent()
  fxChange!: EventEmitter<IRangeChangeEventDetail>;

  /**
   * @event - Emitted when the range receives focus.
   */
  @StencilEvent()
  fxFocus!: EventEmitter<FocusEvent>;

  /**
   * @event - Emitted when the range loses focus.
   */
  @StencilEvent()
  fxBlur!: EventEmitter<FocusEvent>;

  /**
   * @event - Emitted when the user starts moving a knob (drag or keyboard).
   */
  @StencilEvent()
  fxKnobMoveStart!: EventEmitter<IRangeKnobMoveEventDetail>;

  /**
   * @event - Emitted when the user stops moving a knob (drag or keyboard).
   */
  @StencilEvent()
  fxKnobMoveEnd!: EventEmitter<IRangeKnobMoveEventDetail>;

  // --------------------------------------------
  // Lifecycle
  // --------------------------------------------

  componentWillLoad() {
    this.setupDebounce();

    // The `value` prop is a union type (`number | IRangeValue`), which Stencil
    // cannot reliably deserialize from the `value` attribute. Read and parse
    // the raw attribute as the source of truth when the prop is not usable.
    let initial = this.value;
    const attr = this.el.getAttribute("value");
    if (attr !== null) {
      const parsed = this.parseValue(attr);
      if (parsed !== undefined) {
        initial = parsed;
      }
    }

    this.syncFromValue(initial);
  }

  disconnectedCallback() {
    this.releasePointer();
    this.dragRect = null;
    // Cancel any pending debounced change so it cannot emit from a detached element.
    this.emitDebouncedChange.cancel();
    this.hasPendingChange = false;
  }

  // --------------------------------------------
  // Watchers
  // --------------------------------------------

  @Watch("debounce")
  debounceChanged() {
    this.setupDebounce();
  }

  @Watch("value")
  valueChanged(newValue: number | IRangeValue | undefined) {
    // Ignore the echo from our own internal writes; state is already in sync.
    if (this.isInternalValueUpdate) {
      return;
    }
    // The union-typed `value` can arrive as a raw string when set via the
    // attribute at runtime (Stencil can't deserialize `number | IRangeValue`),
    // so parse before syncing to avoid snapping to `min`.
    this.syncFromValue(this.parseValue(newValue as string | number | IRangeValue));
  }

  @Watch("dualKnobs")
  dualKnobsChanged() {
    this.syncFromValue(this.parseValue(this.value as string | number | IRangeValue));
  }

  @Watch("min")
  @Watch("max")
  @Watch("step")
  limitsChanged() {
    // Re-derive the stored knob positions against the new limits/grid so the
    // announced value never falls outside `min`/`max` (and stays on the step
    // grid when `snaps` is enabled).
    this.valueLower = this.constrainValue(this.valueLower);

    if (this.dualKnobs) {
      this.valueUpper = this.constrainValue(this.valueUpper);
      if (this.valueLower > this.valueUpper) {
        const lower = Math.min(this.valueLower, this.valueUpper);
        const upper = Math.max(this.valueLower, this.valueUpper);
        this.valueLower = lower;
        this.valueUpper = upper;
      }
    }

    this.commitValueProp();
    this.syncFormValue();
  }

  // --------------------------------------------
  // Private helpers
  // --------------------------------------------

  private setupDebounce() {
    this.emitDebouncedChange.cancel();

    if (this.debounce > 0) {
      this.emitDebouncedChange = debounce((detail: IRangeChangeEventDetail) => {
        this.hasPendingChange = false;
        this.fxChange.emit(detail);
      }, this.debounce);
    } else {
      this.emitDebouncedChange = Object.assign(
        (detail: IRangeChangeEventDetail) => {
          this.hasPendingChange = false;
          this.fxChange.emit(detail);
        },
        { cancel: () => undefined, flush: () => undefined }
      );
    }
  }

  private flushPendingChange() {
    // Emit any deferred `fxChange` synchronously so it always precedes
    // `fxKnobMoveEnd`, preserving the documented event order even when
    // `debounce > 0`.
    if (this.hasPendingChange) {
      this.emitDebouncedChange.cancel();
      this.hasPendingChange = false;
      this.fxChange.emit({ value: this.currentValue() });
    }
  }

  private parseValue(
    value: string | number | IRangeValue | null | undefined
  ): number | IRangeValue | undefined {
    if (value === null || value === undefined) {
      return undefined;
    }

    if (typeof value === "number") {
      return Number.isNaN(value) ? undefined : value;
    }

    if (typeof value === "object") {
      return value;
    }

    const trimmed = value.trim();
    if (trimmed === "") {
      return undefined;
    }

    try {
      const parsed = JSON.parse(trimmed);
      if (typeof parsed === "number") {
        return parsed;
      }
      if (
        parsed &&
        typeof parsed === "object" &&
        "lower" in parsed &&
        "upper" in parsed
      ) {
        return { lower: Number(parsed.lower), upper: Number(parsed.upper) };
      }
    } catch {
      const num = Number(trimmed);
      if (!Number.isNaN(num)) {
        return num;
      }
    }

    return undefined;
  }

  private syncFromValue(value: number | IRangeValue | undefined) {
    if (this.dualKnobs) {
      const lower =
        value && typeof value === "object" && "lower" in value
          ? value.lower
          : this.min;
      const upper =
        value && typeof value === "object" && "upper" in value
          ? value.upper
          : this.max;

      this.valueLower = this.constrainValue(Math.min(lower, upper));
      this.valueUpper = this.constrainValue(Math.max(lower, upper));
      this.syncFormValue();
      return;
    }

    const single = typeof value === "number" ? value : this.min;
    this.valueLower = this.constrainValue(single);

    this.syncFormValue();
  }

  private syncFormValue() {
    if (!this.internals || typeof this.internals.setFormValue !== "function") {
      return;
    }

    const formValue = this.dualKnobs
      ? JSON.stringify({ lower: this.valueLower, upper: this.valueUpper })
      : String(this.valueLower);

    this.internals.setFormValue(formValue);
  }

  private clamp(value: number): number {
    return Math.min(this.max, Math.max(this.min, value));
  }

  private constrainValue(value: number): number {
    // `snaps` controls whether the value is quantized to the step grid. When
    // it is off, the knob moves freely and is only clamped to `min`/`max`;
    // when on, it snaps to the nearest step.
    return this.snaps ? this.clampToStep(value) : this.clamp(value);
  }

  private clampToStep(value: number): number {
    // The min/max endpoints are always reachable even when `step` does not
    // evenly divide the range, matching native `<input type="range">`.
    if (value <= this.min) {
      return this.min;
    }
    if (value >= this.max) {
      return this.max;
    }

    const step = this.step > 0 ? this.step : 1;
    const steps = Math.round((value - this.min) / step);
    const snapped = this.min + steps * step;
    return this.clamp(Number(snapped.toFixed(10)));
  }

  private ratio(value: number): number {
    if (this.max === this.min) {
      return 0;
    }
    return (this.clamp(value) - this.min) / (this.max - this.min);
  }

  private currentValue(): number | IRangeValue {
    return this.dualKnobs
      ? { lower: this.valueLower, upper: this.valueUpper }
      : this.valueLower;
  }

  private commitValueProp() {
    // Reflect internal state to the public `value` prop without triggering the
    // `value` watcher (which would redundantly re-derive the same state).
    this.isInternalValueUpdate = true;
    this.value = this.currentValue();
    this.isInternalValueUpdate = false;
  }

  /**
   * Resolves the bounded target for a knob without mutating state, so callers
   * can detect a no-op before emitting move/change events.
   */
  private resolveKnobValue(knob: TRangeKnob, next: number): number {
    const constrained = this.constrainValue(next);

    if (knob === "upper") {
      return Math.max(constrained, this.valueLower);
    }

    return this.dualKnobs
      ? Math.min(constrained, this.valueUpper)
      : constrained;
  }

  private updateValue(knob: TRangeKnob, next: number, emit: boolean): boolean {
    const bounded = this.resolveKnobValue(knob, next);

    if (bounded === this.knobValue(knob)) {
      return false;
    }

    if (knob === "upper") {
      this.valueUpper = bounded;
    } else {
      this.valueLower = bounded;
    }

    this.commitValueProp();
    this.syncFormValue();

    if (emit) {
      this.hasPendingChange = true;
      this.emitDebouncedChange({ value: this.currentValue() });
    }

    return true;
  }

  private knobValue(knob: TRangeKnob): number {
    return knob === "upper" ? this.valueUpper : this.valueLower;
  }

  private beginMove(knob: TRangeKnob) {
    this.activeKnob = knob;
    this.isActive = true;
    this.fxKnobMoveStart.emit({ knob, value: this.knobValue(knob) });
  }

  private endMove(knob: TRangeKnob) {
    this.isActive = false;
    this.activeKnob = null;
    this.flushPendingChange();
    this.fxKnobMoveEnd.emit({ knob, value: this.knobValue(knob) });
  }

  // --------------------------------------------
  // Keyboard interaction
  // --------------------------------------------

  private handleKeyDown = (knob: TRangeKnob) => (ev: KeyboardEvent) => {
    if (this.disabled) {
      return;
    }

    const step = this.step > 0 ? this.step : 1;
    const largeStep = Math.max(step, (this.max - this.min) / 10);
    const current = this.knobValue(knob);
    let next: number | null = null;

    switch (ev.key) {
      case "ArrowRight":
      case "ArrowUp":
        next = current + step;
        break;
      case "ArrowLeft":
      case "ArrowDown":
        next = current - step;
        break;
      case "PageUp":
        next = current + largeStep;
        break;
      case "PageDown":
        next = current - largeStep;
        break;
      case "Home":
        next = this.min;
        break;
      case "End":
        next = this.max;
        break;
      default:
        return;
    }

    ev.preventDefault();

    // Do not emit move/change events when the key cannot change the value
    // (e.g. pressing End while already at max).
    if (this.resolveKnobValue(knob, next) === this.knobValue(knob)) {
      return;
    }

    if (!this.isActive) {
      this.beginMove(knob);
    }

    this.updateValue(knob, next, true);
  };

  private handleKeyUp = (knob: TRangeKnob) => (ev: KeyboardEvent) => {
    if (!this.isActive) {
      return;
    }

    const keys = [
      "ArrowRight",
      "ArrowUp",
      "ArrowLeft",
      "ArrowDown",
      "PageUp",
      "PageDown",
      "Home",
      "End",
    ];

    if (keys.includes(ev.key)) {
      this.endMove(knob);
    }
  };

  // --------------------------------------------
  // Pointer interaction
  // --------------------------------------------

  private valueFromClientX(clientX: number): number {
    // Reuse the rect cached at drag start to avoid a layout read per move.
    const rect = this.dragRect ?? this.trackRef?.getBoundingClientRect();
    if (!rect || rect.width === 0) {
      return this.min;
    }

    const ratio = Math.min(1, Math.max(0, (clientX - rect.left) / rect.width));
    return this.min + ratio * (this.max - this.min);
  }

  private nearestKnob(rawValue: number): TRangeKnob {
    if (!this.dualKnobs) {
      return "single";
    }

    const distLower = Math.abs(rawValue - this.valueLower);
    const distUpper = Math.abs(rawValue - this.valueUpper);
    return distUpper < distLower ? "upper" : "lower";
  }

  private handleTrackPointerDown = (ev: PointerEvent) => {
    // Ignore secondary pointers while a drag is already in progress.
    if (this.disabled || this.capturedPointerId !== null) {
      return;
    }

    ev.preventDefault();

    this.dragRect = this.trackRef?.getBoundingClientRect() ?? null;
    const rawValue = this.valueFromClientX(ev.clientX);
    const knob = this.nearestKnob(rawValue);

    // Focus the target knob so keyboard adjustment works after a track click.
    this.knobRefs[knob]?.focus();

    this.beginMove(knob);
    this.updateValue(knob, rawValue, true);
    this.capturePointer(ev.currentTarget as HTMLElement | null, ev.pointerId);
  };

  private handleKnobPointerDown = (knob: TRangeKnob) => (ev: PointerEvent) => {
    // Ignore secondary pointers while a drag is already in progress.
    if (this.disabled || this.capturedPointerId !== null) {
      return;
    }

    ev.preventDefault();
    ev.stopPropagation();

    // Focus the knob so keyboard adjustment works after a pointer interaction.
    const target = ev.currentTarget as HTMLElement | null;
    target?.focus();

    this.dragRect = this.trackRef?.getBoundingClientRect() ?? null;
    this.beginMove(knob);
    this.capturePointer(target, ev.pointerId);
  };

  private handlePointerMove = (ev: PointerEvent) => {
    // Only track the pointer that started the drag (ignore other fingers/pens).
    if (!this.activeKnob || ev.pointerId !== this.capturedPointerId) {
      return;
    }

    const rawValue = this.valueFromClientX(ev.clientX);
    this.updateValue(this.activeKnob, rawValue, true);
  };

  private handlePointerUp = (ev: PointerEvent) => {
    // Only end the drag for the pointer that started it.
    if (!this.activeKnob || ev.pointerId !== this.capturedPointerId) {
      return;
    }

    const knob = this.activeKnob;
    this.releasePointer();
    this.dragRect = null;
    this.endMove(knob);
  };

  private capturePointer(el: HTMLElement | null, pointerId: number) {
    if (!el) {
      return;
    }

    // Pointer capture keeps move/up events flowing to this element even if the
    // pointer leaves it (robust for touch/pen and cross-boundary drags).
    try {
      el.setPointerCapture?.(pointerId);
    } catch {
      // Ignore environments without pointer capture support.
    }

    this.capturedEl = el;
    this.capturedPointerId = pointerId;
    el.addEventListener("pointermove", this.handlePointerMove);
    el.addEventListener("pointerup", this.handlePointerUp);
    el.addEventListener("pointercancel", this.handlePointerUp);
  }

  private releasePointer() {
    const el = this.capturedEl;
    if (el) {
      el.removeEventListener("pointermove", this.handlePointerMove);
      el.removeEventListener("pointerup", this.handlePointerUp);
      el.removeEventListener("pointercancel", this.handlePointerUp);
      if (this.capturedPointerId !== null) {
        try {
          el.releasePointerCapture?.(this.capturedPointerId);
        } catch {
          // Ignore if the capture was already released.
        }
      }
    }
    this.capturedEl = null;
    this.capturedPointerId = null;
  }

  // --------------------------------------------
  // Focus
  // --------------------------------------------

  private handleFocus = (ev: FocusEvent) => {
    if (this.disabled) {
      return;
    }
    // Emit only when focus enters the component, not when it moves between
    // the two knobs in dual-knob mode.
    if (this.hasFocus) {
      return;
    }
    this.hasFocus = true;
    this.fxFocus.emit(ev);
  };

  private handleBlur = (ev: FocusEvent) => {
    const related = ev.relatedTarget as Node | null;
    if (related && this.el.shadowRoot?.contains(related)) {
      // Focus moved to the sibling knob; still inside the component.
      return;
    }
    this.hasFocus = false;
    this.fxBlur.emit(ev);
  };

  // --------------------------------------------
  // Render helpers
  // --------------------------------------------

  private renderTicks() {
    if (!this.snaps || !this.ticks || this.step <= 0) {
      return null;
    }

    const count = Math.floor((this.max - this.min) / this.step);
    const values: number[] = [];

    for (let i = 0; i <= count; i++) {
      values.push(this.clamp(this.min + i * this.step));
    }

    // Always mark the max endpoint, even when `step` does not divide the range
    // (the knob can now reach `max`, so it needs a tick).
    if (values[values.length - 1] < this.max) {
      values.push(this.max);
    }

    const items = values.map((tickValue, i) => {
      const active = this.dualKnobs
        ? tickValue >= this.valueLower && tickValue <= this.valueUpper
        : tickValue <= this.valueLower;

      return (
        <span
          key={i}
          part={active ? "tick tick-active" : "tick"}
          data-active={active ? "" : undefined}
          data-value={tickValue}
          style={{ left: `${this.ratio(tickValue) * 100}%` }}
        />
      );
    });

    return (
      <div part="ticks" aria-hidden="true">
        {items}
      </div>
    );
  }

  private knobAriaLabel(knob: TRangeKnob): string {
    // Forward a consumer-provided host label; otherwise fall back to a default
    // so every `role="slider"` element always has an accessible name.
    const hostLabel = this.el.getAttribute("aria-label");

    if (this.dualKnobs) {
      const which = knob === "upper" ? "upper" : "lower";
      return hostLabel ? `${hostLabel} ${which} value` : `${which} value`;
    }

    return hostLabel ?? "Value";
  }

  private knobAriaRange(knob: TRangeKnob): { min: number; max: number } {
    // Each knob is bounded by its sibling in dual mode so assistive tech
    // announces the reachable range (and never an out-of-bounds value).
    if (this.dualKnobs && knob === "lower") {
      return { min: this.min, max: this.valueUpper };
    }
    if (this.dualKnobs && knob === "upper") {
      return { min: this.valueLower, max: this.max };
    }
    return { min: this.min, max: this.max };
  }

  private renderKnob(knob: TRangeKnob) {
    const value = this.knobValue(knob);
    const ratio = this.ratio(value);
    const label = knob === "single" ? "lower" : knob;
    const knobPart = knob === "upper" ? "knob knob-upper" : "knob knob-lower";
    const range = this.knobAriaRange(knob);

    return (
      <div
        part={knobPart}
        role="slider"
        tabindex={this.disabled ? -1 : 0}
        data-knob={label}
        data-active={this.activeKnob === knob ? "" : undefined}
        aria-label={this.knobAriaLabel(knob)}
        aria-valuemin={range.min}
        aria-valuemax={range.max}
        aria-valuenow={value}
        aria-disabled={this.disabled ? "true" : null}
        aria-orientation="horizontal"
        style={{ left: `${ratio * 100}%` }}
        ref={(el) => {
          if (el) {
            this.knobRefs[knob] = el as HTMLElement;
          }
        }}
        onKeyDown={this.handleKeyDown(knob)}
        onKeyUp={this.handleKeyUp(knob)}
        onPointerDown={this.handleKnobPointerDown(knob)}
        onFocus={this.handleFocus}
        onBlur={this.handleBlur}
      >
        {this.pin && (
          <span part="pin" aria-hidden="true">
            {this.pinFormatter(value)}
          </span>
        )}
      </div>
    );
  }

  // --------------------------------------------
  // Render
  // --------------------------------------------

  render() {
    const activeStart = this.dualKnobs ? this.ratio(this.valueLower) : 0;
    const activeEnd = this.dualKnobs
      ? this.ratio(this.valueUpper)
      : this.ratio(this.valueLower);

    return (
      <Host
        data-disabled={this.disabled ? "" : undefined}
        data-dual-knobs={this.dualKnobs ? "" : undefined}
        data-active={this.isActive ? "" : undefined}
        data-color={this.color}
        aria-disabled={this.disabled ? "true" : null}
        name={this.name}
        class={buildColorClasses(this.color as TThemeColors)}
      >
        <div part="base">
          <div
            part="track"
            ref={(el) => (this.trackRef = el as HTMLElement)}
            onPointerDown={this.handleTrackPointerDown}
          >
            <div
              part="active-track"
              style={{
                left: `${activeStart * 100}%`,
                width: `${(activeEnd - activeStart) * 100}%`,
              }}
            />
            {this.renderTicks()}
          </div>
          {this.renderKnob(this.dualKnobs ? "lower" : "single")}
          {this.dualKnobs && this.renderKnob("upper")}
        </div>
      </Host>
    );
  }
}
